import React, { useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  alpha,
} from "@mui/material";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";
import { EventRepeat } from "@mui/icons-material";
import { Transaction } from "../utils/types";
import { useTheme } from "@mui/material";
import { getStatusMeta, formatDateTime, operationLabels } from "../utils/utils";
import { translate } from "../utils/currency";

/**
 * Columns are declared up front so they can be toggled. Rarely used fields are
 * hidden by default, which keeps the table inside the viewport without
 * horizontal scrolling; everything else is one click away.
 */
type ColumnId =
  | "id"
  | "requisites"
  | "method"
  | "status"
  | "bank"
  | "orderId"
  | "type"
  | "user"
  | "amount"
  | "paid"
  | "rate"
  | "date";

const COLUMNS: { id: ColumnId; label: string; defaultHidden?: boolean }[] = [
  { id: "id", label: "Transaction ID" },
  { id: "requisites", label: "Requisites" },
  { id: "method", label: "Method" },
  { id: "status", label: "Status" },
  { id: "bank", label: "Bank", defaultHidden: true },
  { id: "orderId", label: "Order ID" },
  { id: "type", label: "Type" },
  { id: "user", label: "User", defaultHidden: true },
  { id: "amount", label: "Amount" },
  { id: "paid", label: "Confirmed" },
  { id: "rate", label: "Rate / fee", defaultHidden: true },
  { id: "date", label: "Date" },
];

const VISIBILITY_KEY = "merchant.transactions.columns";
const DENSITY_KEY = "merchant.transactions.density";

const readVisibility = (): Record<string, boolean> => {
  const defaults = Object.fromEntries(
    COLUMNS.map((column) => [column.id, !column.defaultHidden]),
  );
  try {
    const saved = localStorage.getItem(VISIBILITY_KEY);
    return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  } catch {
    return defaults;
  }
};

const TransactionsTable: React.FC<{
  transactions: Transaction[];
}> = ({ transactions }) => {
  const theme = useTheme();
  const [visible, setVisible] =
    useState<Record<string, boolean>>(readVisibility);
  const [dense, setDense] = useState<boolean>(
    () => localStorage.getItem(DENSITY_KEY) !== "comfortable",
  );
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const shownColumns = useMemo(
    () => COLUMNS.filter((column) => visible[column.id]),
    [visible],
  );

  const toggleColumn = useCallback((id: ColumnId) => {
    setVisible((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleDensity = (_: unknown, value: "dense" | "comfortable" | null) => {
    if (!value) return;
    setDense(value === "dense");
    localStorage.setItem(DENSITY_KEY, value);
  };

  const cellPadding = dense ? "6px 10px" : "12px 12px";

  const copyButton = (value: string, label: string) => (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        aria-label={`Copy ${label}`}
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          navigator.clipboard.writeText(value);
        }}
        sx={{ width: 30, height: 30, flexShrink: 0 }}
      >
        <ContentCopyRounded sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );

  const renderCell = (column: ColumnId, t: Transaction) => {
    switch (column) {
      case "id":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: 150,
            }}
          >
            <Tooltip title={t.id}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontFamily: "monospace", fontSize: 12.5 }}
              >
                {t.id}
              </Typography>
            </Tooltip>
            {copyButton(t.id, "transaction ID")}
          </Box>
        );

      case "requisites":
        return (
          <Box sx={{ maxWidth: 160 }}>
            <Tooltip
              title={
                (t.payment_method === "bol_qr_code" ? t.qr_code : t.number) ||
                "—"
              }
            >
              <Typography
                variant="body2"
                noWrap
                sx={{ fontFamily: "monospace", fontSize: 12.5 }}
              >
                {(t.payment_method === "bol_qr_code" ? t.qr_code : t.number) ||
                  "—"}
              </Typography>
            </Tooltip>
            <Tooltip title={t.fullname || "—"}>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block" }}
              >
                {t.fullname || "—"}
              </Typography>
            </Tooltip>
          </Box>
        );

      case "method":
        return (
          <Typography variant="body2" noWrap>
            {translate(t.payment_method)}
          </Typography>
        );

      case "status": {
        const status = getStatusMeta(t.status, theme.palette.mode);
        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1,
              py: 0.4,
              borderRadius: "8px",
              whiteSpace: "nowrap",
              backgroundColor: alpha(status.color, 0.14),
              border: `1px solid ${alpha(status.color, 0.32)}`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: status.color,
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: status.textColor }}
            >
              {status.label}
            </Typography>
          </Box>
        );
      }

      case "bank":
        return (
          <Typography variant="body2" noWrap>
            {t.bank_name || "—"}
          </Typography>
        );

      case "orderId":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: 150,
            }}
          >
            <Tooltip title={t.merchant_order_id}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontFamily: "monospace", fontSize: 12.5 }}
              >
                {t.merchant_order_id}
              </Typography>
            </Tooltip>
            {copyButton(t.merchant_order_id, "order ID")}
          </Box>
        );

      case "type":
        return (
          <Typography variant="body2" noWrap>
            {operationLabels[t.transaction_type] || t.transaction_type}
          </Typography>
        );

      case "user":
        return (
          <Typography
            variant="body2"
            noWrap
            sx={{ fontFamily: "monospace", fontSize: 12.5, maxWidth: 120 }}
          >
            {t.user_id || "—"}
          </Typography>
        );

      case "amount":
        return (
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
          >
            {t.start_amount.toLocaleString("en-US")}{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {t.currency}
            </Typography>
          </Typography>
        );

      case "paid":
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              color: t.paid_amount
                ? theme.palette.semanticText.success
                : "text.secondary",
            }}
          >
            {t.paid_amount?.toLocaleString("en-US") || "0"}
          </Typography>
        );

      case "rate":
        return (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "nowrap", textAlign: "right" }}
          >
            {t.our_rate}%{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              / {t.our_fee?.toLocaleString("en-US") ?? 0}
            </Typography>
          </Typography>
        );

      case "date": {
        const date = formatDateTime(t.created_at);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontSize: 12.5 }} noWrap>
                {date.date}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11 }}
              >
                {date.time}
              </Typography>
            </Box>
            {t.updated_at && (
              <Tooltip
                title={`Updated: ${formatDateTime(t.updated_at).date} ${
                  formatDateTime(t.updated_at).time
                }`}
              >
                <EventRepeat
                  sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const isNumeric = (id: ColumnId) =>
    id === "amount" || id === "paid" || id === "rate";

  return (
    <Paper
      sx={{
        width: "100%",
        // Заполняет обёртку и не растёт под содержимое: иначе Paper вытягивался
        // на всю таблицу, обрезался родителем, и прокручивать было нечего —
        // maxHeight у TableContainer считался от этой раздутой высоты.
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        boxShadow: "none",
        borderRadius: "15px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {transactions.length} shown
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={dense ? "dense" : "comfortable"}
            onChange={handleDensity}
            aria-label="Row density"
          >
            <ToggleButton value="dense" sx={{ py: 0.4 }}>
              Compact
            </ToggleButton>
            <ToggleButton value="comfortable" sx={{ py: 0.4 }}>
              Comfortable
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Configure columns">
            <IconButton
              aria-label="Configure columns"
              onClick={(event) => setColumnsAnchor(event.currentTarget)}
              sx={{ border: `1px solid ${theme.palette.divider}` }}
            >
              <ViewColumnRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 0.5, display: "block", fontWeight: 700 }}
        >
          Columns
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        {COLUMNS.map((column) => (
          <MenuItem
            key={column.id}
            dense
            onClick={() => toggleColumn(column.id)}
          >
            <Checkbox
              size="small"
              checked={Boolean(visible[column.id])}
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemText primary={column.label} />
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{
          border: 0,
          borderRadius: 0,
          // Заполняет обёртку, а не долю экрана: прокрутка нужна здесь,
          // потому что залипающая шапка требует, чтобы скроллером был
          // именно TableContainer.
          maxHeight: "100%",
        }}
      >
        <Table stickyHeader size={dense ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              {shownColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={isNumeric(column.id) ? "right" : "left"}
                  sx={{ whiteSpace: "nowrap", padding: cellPadding }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {transactions.map((t) => (
              <TableRow key={t.id} hover>
                {shownColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={isNumeric(column.id) ? "right" : "left"}
                    sx={{ padding: cellPadding }}
                  >
                    {renderCell(column.id, t)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TransactionsTable;
